import React from "react";
import {
  Box,
  Paper,
  Typography,
  Icon,
  alpha,
  Switch,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Chip,
  Tooltip as CustomTooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

// TODO: These types need to be properly defined. Using 'any' for now as requested.
interface RoleAndAccessProps {
  staffData: any;
  errors: any;
  loading: boolean;
  roles: any[];
  handleSelectChange: any;
  handleStatusChange: any;
  setPermissionViewOpen: any;
}

export const StaffBuilderRoleAndAccess: React.FC<RoleAndAccessProps> = ({
  staffData,
  errors,
  loading,
  roles,
  handleSelectChange,
  handleStatusChange,
  setPermissionViewOpen,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid",
        borderColor: alpha("#0891B2", 0.1),
        borderRadius: 1,
        height: "100%",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: "#0891B2",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontFamily: "Montserrat",
          }}
        >
          <Icon>security</Icon>
          System Access & Permissions
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Montserrat" }}
        >
          Configure system access, role, and permissions
        </Typography>
      </Box>

      {/* Account Status */}
      <Box sx={{ mb: 2.5 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
            borderColor: staffData.is_active ? "#059669" : "#DC2626",
            transition: "all 0.2s ease-in-out",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: staffData.is_active
                  ? alpha("#059669", 0.1)
                  : alpha("#DC2626", 0.1),
              }}
            >
              <Icon
                sx={{
                  color: staffData.is_active ? "#059669" : "#DC2626",
                  fontSize: 24,
                }}
              >
                {staffData.is_active ? "check_circle" : "cancel"}
              </Icon>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  fontFamily: "Montserrat",
                  fontSize: "0.95rem",
                }}
              >
                Account Status
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                }}
              >
                {staffData.is_store_owner
                  ? "Store owner account is always active"
                  : staffData.is_active
                  ? "Account is active and can access the system"
                  : "Account is inactive and cannot access the system"}
              </Typography>
            </Box>
            {!staffData.is_store_owner && (
              <ToggleButtonGroup
                value={staffData.is_active}
                exclusive
                onChange={handleStatusChange}
                aria-label="staff status"
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "4px !important",
                    border: "1px solid",
                    borderColor: "divider",
                    fontFamily: "Montserrat",
                    fontSize: "0.875rem",
                    "&.Mui-selected": {
                      "&.active": {
                        bgcolor: alpha("#059669", 0.1),
                        color: "#059669",
                        borderColor: "#059669",
                        "&:hover": {
                          bgcolor: alpha("#059669", 0.2),
                        },
                      },
                      "&.inactive": {
                        bgcolor: alpha("#DC2626", 0.1),
                        color: "#DC2626",
                        borderColor: "#DC2626",
                        "&:hover": {
                          bgcolor: alpha("#DC2626", 0.2),
                        },
                      },
                    },
                  },
                }}
              >
                <ToggleButton
                  value={true}
                  className="active"
                  sx={{
                    textTransform: "none",
                    gap: 0.5,
                  }}
                >
                  <Icon fontSize="small">check_circle</Icon>
                  Active
                </ToggleButton>
                <ToggleButton
                  value={false}
                  className="inactive"
                  sx={{
                    textTransform: "none",
                    gap: 0.5,
                  }}
                >
                  <Icon fontSize="small">cancel</Icon>
                  Inactive
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Role Assignment */}
      {staffData.is_store_owner ? (
        <Box sx={{ mb: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.05),
              borderColor: "#0891B2",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#0891B2", 0.1),
                }}
              >
                <Icon sx={{ color: "#0891B2", fontSize: 24 }}>
                  admin_panel_settings
                </Icon>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    fontFamily: "Montserrat",
                    fontSize: "0.95rem",
                  }}
                >
                  Store Owner
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "0.875rem",
                  }}
                >
                  This is the administrator account for this store
                </Typography>
              </Box>
              <CustomTooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "Montserrat" }}
                    >
                      Store owner permissions cannot be modified
                    </Typography>
                  </Box>
                }
                placement="top"
              >
                <Icon color="action" sx={{ fontSize: 20 }}>
                  info
                </Icon>
              </CustomTooltip>
            </Box>
          </Paper>
        </Box>
      ) : (
        <FormControl fullWidth error={!!errors.role} sx={{ mb: 2.5 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontFamily: "Montserrat",
                fontSize: "0.95rem",
                mb: 0.5,
              }}
            >
              Role Assignment
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
              }}
            >
              Select a role to define base permissions
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Select
              value={staffData.role}
              onChange={handleSelectChange("role")}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0891B2", fontSize: 20 }}>badge</Icon>
                </InputAdornment>
              }
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                  py: 1.5,
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontFamily: "Montserrat",
                    fontSize: "0.875rem",
                  }}
                >
                  Select a role
                </Typography>
              </MenuItem>
              {roles.map((role) => (
                <MenuItem
                  key={role._id}
                  value={role._id}
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: alpha("#0891B2", 0.08),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha("#0891B2", 0.1),
                      }}
                    >
                      <Icon fontSize="small" sx={{ color: "#0891B2" }}>
                        verified_user
                      </Icon>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                          fontFamily: "Montserrat",
                          fontSize: "0.875rem",
                        }}
                      >
                        {role.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily: "Montserrat",
                          fontSize: "0.75rem",
                        }}
                      >
                        {role.permissions?.length || 0} permissions assigned
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${role.permissions?.length || 0} permissions`}
                      sx={{
                        bgcolor: alpha("#0891B2", 0.1),
                        color: "#0891B2",
                        fontFamily: "Montserrat",
                        fontSize: "0.75rem",
                      }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {staffData.role && (
              <Button
                size="small"
                startIcon={<Icon fontSize="small">admin_panel_settings</Icon>}
                onClick={() => setPermissionViewOpen(true)}
                sx={{
                  bgcolor: alpha("#0891B2", 0.08),
                  color: "#0891B2",
                  "&:hover": {
                    bgcolor: alpha("#0891B2", 0.12),
                  },
                  minWidth: "auto",
                  px: 2,
                  borderRadius: 1,
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                }}
              >
                Manage
              </Button>
            )}
          </Box>
          {errors.role && (
            <FormHelperText error sx={{ fontFamily: "Montserrat", mt: 0.5 }}>
              {errors.role}
            </FormHelperText>
          )}
        </FormControl>
      )}
    </Paper>
  );
};
